var __makeTemplateObject = (this && this.__makeTemplateObject) || function (cooked, raw) {
    if (Object.defineProperty) { Object.defineProperty(cooked, "raw", { value: raw }); } else { cooked.raw = raw; }
    return cooked;
};
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __spreadArrays = (this && this.__spreadArrays) || function () {
    for (var s = 0, i = 0, il = arguments.length; i < il; i++) s += arguments[i].length;
    for (var r = Array(s), k = 0, i = 0; i < il; i++)
        for (var a = arguments[i], j = 0, jl = a.length; j < jl; j++, k++)
            r[k] = a[j];
    return r;
};
import React, { useRef, useLayoutEffect, useCallback } from 'react';
import styled, { css } from 'styled-components';
import deepEqual from 'fast-deep-equal';
import { TreeNode } from './TreeNode';
import { TREE_VIEW_CHECK_BEHAVIOR, TREE_VIEW_EXPAND_BEHAVIOR } from './types';
import { computeCheckedIds, isNodeChecked } from './selectionHelpers';
import { NodeData } from './nodeData';
import { Spinner } from "../Spinner";
import { Text } from "../Text";
import { INDENT, View, VIEW_ALIGN, VIEW_CONTENT_MOD } from "../View";
import { Overlay, OVERLAY_CONTENT_MOD, OVERLAY_VIEW } from "../Overlay";
import { createUIElement, useStateValue } from "../helpers";
var ScrollableWrapper = styled.div(templateObject_5 || (templateObject_5 = __makeTemplateObject(["\n    overflow-y: auto;\n    overflow-x: hidden;\n    position: relative;\n\n    ", "\n\n    ", "\n"], ["\n    overflow-y: auto;\n    overflow-x: hidden;\n    position: relative;\n\n    ",
    "\n\n    ",
    "\n"])), function (_a) {
    var fullSize = _a.fullSize;
    return fullSize && css(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n            height: 100%;\n            width: 100%;\n            box-sizing: border-box;\n        "], ["\n            height: 100%;\n            width: 100%;\n            box-sizing: border-box;\n        "])));
}, function (_a) {
    var bordered = _a.bordered, error = _a.error, disabled = _a.disabled, theme = _a.theme;
    if (disabled && bordered) {
        return css(templateObject_2 || (templateObject_2 = __makeTemplateObject(["\n                border: 1px solid ", ";\n            "], ["\n                border: 1px solid ", ";\n            "])), theme.colors.borders.disabled);
    }
    if (!disabled && error) {
        return css(templateObject_3 || (templateObject_3 = __makeTemplateObject(["\n                border: 1px solid ", ";\n                background-color: ", ";\n            "], ["\n                border: 1px solid ", ";\n                background-color: ", ";\n            "])), theme.colorBorderError, theme.colorBackgroundError);
    }
    return (bordered && css(templateObject_4 || (templateObject_4 = __makeTemplateObject(["\n                border: 1px solid ", ";\n            "], ["\n                border: 1px solid ", ";\n            "])), theme.colors.borders.normal));
});
var Body = styled(View)(templateObject_6 || (templateObject_6 = __makeTemplateObject(["\n    min-width: 100%;\n    flex: 1;\n    position: relative;\n    display: flex;\n"], ["\n    min-width: 100%;\n    flex: 1;\n    position: relative;\n    display: flex;\n"])));
var NoDataWrapper = styled(View)(templateObject_7 || (templateObject_7 = __makeTemplateObject(["\n    height: ", ";\n"], ["\n    height: ", ";\n"])), function (_a) {
    var theme = _a.theme;
    return theme.spacing.xl;
});
export var TreeView = createUIElement('TreeView', function (_a, ref) {
    var className = _a.className, nodeData = _a.nodeData, nodeRenderer = _a.nodeRenderer, onSelect = _a.onSelect, onActivateNode = _a.onActivateNode, onToggleExpandNode = _a.onToggleExpandNode, onCheck = _a.onCheck, onExpand = _a.onExpand, onContextMenu = _a.onContextMenu, _b = _a.selected, selected = _b === void 0 ? [] : _b, _c = _a.checked, checked = _c === void 0 ? [] : _c, _d = _a.expanded, expanded = _d === void 0 ? [] : _d, loading = _a.loading, iconRenderer = _a.iconRenderer, _e = _a.hideIcon, hideIcon = _e === void 0 ? false : _e, _f = _a.bordered, bordered = _f === void 0 ? false : _f, _g = _a.checkable, checkable = _g === void 0 ? false : _g, _h = _a.checkableConfig, checkableConfig = _h === void 0 ? {
        checkableNode: false,
        checkableHighlighting: false,
        activateNodeOnCheck: true
    } : _h, _j = _a.checkBehavior, checkBehavior = _j === void 0 ? TREE_VIEW_CHECK_BEHAVIOR.simple : _j, _k = _a.disabled, disabled = _k === void 0 ? false : _k, error = _a.error, _l = _a.expandAll, expandAll = _l === void 0 ? false : _l, isNodeLoading = _a.isNodeLoading, isNodeDisabled = _a.isNodeDisabled, nodeWarningGetter = _a.nodeWarningGetter, autoHeight = _a.autoHeight, style = _a.style, _m = _a.noDataPlaceholder, noDataPlaceholder = _m === void 0 ? 'No data to display' : _m, _o = _a.expandBehavior, expandBehaviorProp = _o === void 0 ? TREE_VIEW_EXPAND_BEHAVIOR.none : _o, onMouseEnter = _a.onMouseEnter, onMouseLeave = _a.onMouseLeave, rest = __rest(_a, ["className", "nodeData", "nodeRenderer", "onSelect", "onActivateNode", "onToggleExpandNode", "onCheck", "onExpand", "onContextMenu", "selected", "checked", "expanded", "loading", "iconRenderer", "hideIcon", "bordered", "checkable", "checkableConfig", "checkBehavior", "disabled", "error", "expandAll", "isNodeLoading", "isNodeDisabled", "nodeWarningGetter", "autoHeight", "style", "noDataPlaceholder", "expandBehavior", "onMouseEnter", "onMouseLeave"]);
    var dataSelectorId = rest['data-selector-id'] || 'treeview';
    var expandBehavior = expandAll ? TREE_VIEW_EXPAND_BEHAVIOR.expandAll : expandBehaviorProp;
    var prevNodeData = useRef([]);
    var prevSelected = useRef([]);
    var getChecked = useStateValue(checked, [checked]).getValue;
    var _p = useStateValue(expanded, [expanded]), getExpanded = _p.getValue, setExpanded = _p.setValue;
    if (!onExpand) {
        onExpand = setExpanded;
    }
    var onCheckedNode = useCallback(function (node) {
        var _a;
        onCheck === null || onCheck === void 0 ? void 0 : onCheck(computeCheckedIds(checkBehavior, node, (_a = getChecked()) !== null && _a !== void 0 ? _a : []));
    }, [onCheck, checkBehavior]);
    var onActivateNodeCallback = useCallback(function (node) {
        onSelect === null || onSelect === void 0 ? void 0 : onSelect([node.id]);
        onActivateNode === null || onActivateNode === void 0 ? void 0 : onActivateNode(node);
    }, [onSelect, onActivateNode]);
    var toggleExpandNodeCallback = useCallback(function (node) {
        var prevExpanded = getExpanded();
        var foundIndex = prevExpanded.findIndex(function (expandedId) { return expandedId === node.id; });
        var isExpanded = foundIndex === -1;
        if (!isExpanded) {
            var newExpanded = prevExpanded.slice();
            newExpanded.splice(foundIndex, 1);
            onExpand === null || onExpand === void 0 ? void 0 : onExpand(__spreadArrays(newExpanded));
        }
        else {
            onExpand === null || onExpand === void 0 ? void 0 : onExpand(__spreadArrays(prevExpanded, [node.id]));
        }
        onToggleExpandNode === null || onToggleExpandNode === void 0 ? void 0 : onToggleExpandNode(node, isExpanded);
    }, [onExpand, onToggleExpandNode]);
    var expandNodes = function (nodes) {
        var result = __spreadArrays(getExpanded());
        nodes.forEach(function (node) {
            if (result.findIndex(function (expandedId) { return expandedId === node.id; }) === -1) {
                result.push(node.id);
            }
        });
        onExpand === null || onExpand === void 0 ? void 0 : onExpand(result);
    };
    var expandToNodes = function (nodeIds) {
        var result = [];
        nodeIds.forEach(function (nodeId) {
            var foundNode;
            nodeData.some(function (node) { return (foundNode = node.getById(nodeId)); });
            if (foundNode) {
                result.push.apply(result, foundNode.getAncestors().slice(1));
            }
        });
        expandNodes(result);
    };
    var needExpandAll = function () {
        return (expandBehavior === TREE_VIEW_EXPAND_BEHAVIOR.expandAll &&
            !NodeData.equalNodes(prevNodeData.current, nodeData));
    };
    var needExpandToSelected = function () {
        return ((expandBehavior === TREE_VIEW_EXPAND_BEHAVIOR.expandToSelected ||
            expandBehavior === TREE_VIEW_EXPAND_BEHAVIOR.expandToSelectedAndChecked) &&
            selected &&
            !deepEqual(prevSelected.current, selected) &&
            selected.every(function (selectedId) { return nodeData.some(function (node) { return node.getById(selectedId); }); }));
    };
    var needExpandToChecked = function () {
        return ((expandBehavior === TREE_VIEW_EXPAND_BEHAVIOR.expandToChecked ||
            expandBehavior === TREE_VIEW_EXPAND_BEHAVIOR.expandToSelectedAndChecked) &&
            checked &&
            checked.every(function (checkedId) { return nodeData.some(function (node) { return node.getById(checkedId); }); }));
    };
    var getFlattenNodesForRender = function () {
        return nodeData.reduce(function (acc, currNode) {
            var _a;
            var current;
            var visitors = [currNode];
            while ((current = visitors.pop())) {
                acc.push(current);
                if (getExpanded().includes(current.id)) {
                    visitors.push.apply(visitors, ((_a = current.children) !== null && _a !== void 0 ? _a : []).slice().reverse());
                }
            }
            return acc;
        }, []);
    };
    useLayoutEffect(function () {
        if (needExpandAll()) {
            prevNodeData.current = nodeData;
            onExpand === null || onExpand === void 0 ? void 0 : onExpand(nodeData.reduce(function (acc, node) {
                node.getDescendants().forEach(function (node) {
                    var _a;
                    if (!!((_a = node.children) === null || _a === void 0 ? void 0 : _a.length)) {
                        acc.push(node.id);
                    }
                });
                return acc;
            }, []));
            return;
        }
        if (needExpandToSelected()) {
            prevSelected.current = selected;
            expandToNodes(selected);
        }
    });
    useLayoutEffect(function () {
        if (needExpandToChecked()) {
            expandToNodes(checked);
        }
    }, []);
    var renderData = getFlattenNodesForRender();
    return (React.createElement(ScrollableWrapper, __assign({}, rest, { "data-selector-id": dataSelectorId, className: "vm-TreeView " + className, bordered: bordered, disabled: disabled, error: !!error, fullSize: loading, onMouseEnter: onMouseEnter, onMouseLeave: onMouseLeave, ref: ref, style: style }),
        loading && (React.createElement(Overlay, { "data-selector-id": dataSelectorId + "-spinner-wrapper", view: OVERLAY_VIEW.soft, contentMod: OVERLAY_CONTENT_MOD.center },
            React.createElement(Spinner, { size: 50 }))),
        !loading && (React.createElement(Body, { "data-selector-id": dataSelectorId + "-body", align: VIEW_ALIGN.start },
            !renderData.length && (React.createElement(NoDataWrapper, { spaceHorizontal: INDENT.s, contentMod: VIEW_CONTENT_MOD.center },
                React.createElement(Text, { "data-selector-id": dataSelectorId + "-no-data" }, noDataPlaceholder))),
            renderData.map(function (node) {
                var _a, _b;
                return (React.createElement(TreeNode, { key: String(node.id), "data-selector-id": dataSelectorId + "-node" + node
                        .getAncestors()
                        .reverse()
                        .map(function (node) { return "-id" + node.id; })
                        .join(''), node: node, selected: selected.includes(node.id), renderer: nodeRenderer, autoHeight: autoHeight, depth: node.depth, onActivateNode: onActivateNodeCallback, onContextMenu: onContextMenu, iconRenderer: iconRenderer, hideIcon: hideIcon, checkable: checkable, checked: isNodeChecked(node, getChecked(), checkBehavior), onCheckedNode: onCheckedNode, checkableConfig: {
                        checkableNode: checkableConfig.checkableNode,
                        checkableHighlighting: checkableConfig.checkableHighlighting,
                        activateNodeOnCheck: checkableConfig.activateNodeOnCheck
                    }, disabled: disabled || ((_a = isNodeDisabled === null || isNodeDisabled === void 0 ? void 0 : isNodeDisabled(node)) !== null && _a !== void 0 ? _a : false), expanded: getExpanded().includes(node.id), loading: (_b = isNodeLoading === null || isNodeLoading === void 0 ? void 0 : isNodeLoading(node)) !== null && _b !== void 0 ? _b : false, warning: (nodeWarningGetter === null || nodeWarningGetter === void 0 ? void 0 : nodeWarningGetter(node)) || '', onExpandNode: toggleExpandNodeCallback }));
            })))));
});
var templateObject_1, templateObject_2, templateObject_3, templateObject_4, templateObject_5, templateObject_6, templateObject_7;
//# sourceMappingURL=TreeView.js.map